import { Icon, Text, useShowNotification } from '@inflearn/ds-react';
import { faBell, faCheck } from '@inflearn/pro-regular-svg-icons';
import { Button } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'next-i18next';
import { API_ENDPOINT } from '../../../../../../resources/@types/apiEndpoint';
import { useClientApiV1FollowsUserIdUpdateMutation } from '../../../../../../resources/@types/uccSchema';
import { logger } from '../../../../../../resources/Logger';
import useCheckFollowing from '../../../../../hooks/useCheckFollowing';
import useCheckLoginSession from '../../../../../hooks/useCheckLoginSession';
import useQueryParams from '../../../../../hooks/useQueryParams';
import useUserProfileQuery from '../../../../../hooks/useUserProfileQuery';
import { useLoginModalContext } from '../../../../../providers/ControlLoginModalContextProvider';
import { eventNames } from '../../../../../utils/GTM/eventNames';
import { useTrackProfileEvent } from '../../../../../utils/GTM/useTrackProfileEvent';
import SuccessFollowModal from './SuccessFollowModal';

export function FollowingButton() {
  const { id } = useQueryParams();
  const { data: profile } = useUserProfileQuery({ id: Number(id) });
  const userId = profile?.id;
  const { data: status } = useCheckFollowing(String(userId));
  const isFollowing = Boolean(status?.isFollowing);
  const isInstructor = profile?.isInstructor;

  const { t } = useTranslation();
  const { isLoggedIn } = useCheckLoginSession();
  const { showNotification } = useShowNotification();
  const { trackMixpanelEvent } = useTrackProfileEvent();

  const { open: openLoginModal } = useLoginModalContext();
  const [isSuccessFollowModalOpened, successFollowModal] = useDisclosure(false);

  const queryClient = useQueryClient();
  const { mutate: updateFollow } = useClientApiV1FollowsUserIdUpdateMutation({
    url: API_ENDPOINT.CLIENT_V1_FOLLOWS_USER_ID(String(userId)),
    options: {
      onSuccess: async (_, variables) => {
        await queryClient.invalidateQueries({
          queryKey: [API_ENDPOINT.CLIENT_V1_USERS_USERID_IS_FOLLOWING(String(userId))],
        });
        trackMixpanelEvent(eventNames.CLICK_FOLLOW_BUTTON, {
          is_following: variables.isFollowing,
        });
        successFollowModal.open();
      },
      onError: (error) => {
        showNotification({
          type: 'error',
          title: t('팔로우 요청에 실패했어요.'),
          message: t('네트워크 연결 확인 후 다시 한 번 시도해주세요.'),
        });
        logger.info(
          `[url]: ${API_ENDPOINT.CLIENT_V1_FOLLOWS_USER_ID(String(userId))}, 호출 에러: ${error}`,
        );
      },
    },
  });

  const handleFollowClick = (updateValue: boolean) => {
    updateFollow({ isFollowing: updateValue });
  };

  if (!isLoggedIn) {
    return (
      <>
        <Button
          variant="outline"
          color="infgreen"
          radius="md"
          onClick={openLoginModal}
          aria-label={t('비로그인 유저 로그인 모달 열기')}
        >
          <Icon icon={faBell} size="md" aria-hidden />
          <Text component="span" ml={6} aria-hidden>
            {t('팔로우')}
          </Text>
        </Button>
      </>
    );
  }

  return (
    <>
      {isFollowing ? (
        <Button
          variant="outline"
          color="infgreen"
          size="sm"
          radius="md"
          onClick={() => handleFollowClick(false)}
          aria-label={t('팔로우 취소하기')}
        >
          <Icon icon={faCheck} size="md" aria-hidden />
          <Text component="span" ml={6} aria-hidden>
            {t('팔로잉')}
          </Text>
        </Button>
      ) : (
        <Button
          variant="outline"
          color="infgreen"
          size="sm"
          radius="md"
          onClick={() => handleFollowClick(true)}
          aria-label={t('팔로우 하기')}
        >
          <Icon icon={faBell} size="md" aria-hidden />
          <Text component="span" ml={6} aria-hidden>
            {t('팔로우')}
          </Text>
        </Button>
      )}
      <SuccessFollowModal
        userName={profile?.name ?? ''}
        isInstructor={isInstructor ?? false}
        isFollowing={isFollowing}
        onClosed={successFollowModal.close}
        isSuccessFollowModalOpened={isSuccessFollowModalOpened}
      />
    </>
  );
}
